import React, {useState} from 'react';
import {debounce} from 'lodash';

import {camel} from 'src/utils';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {useApiQuery} from 'src/hooks/useApiQuery';

import type {Template} from 'src/types/scheduler/templates';

import {
  Button,
  TruncatedTextWithTooltip,
  Pagination,
  BodyMedium,
  SubTitleExtraSmall,
  SearchInput,
  ButtonTabs,
  ButtonTab,
  SingleCell,
  Table,
  BasicSingleCell,
  TableBottomBar,
  type GenericHeaderItems,
  CircularLoader,
  EmptyState,
} from '@spaced-out/ui-design-system';

import css from './template-selector.css';
import usePrivilege from 'src/hooks/usePrivilege';


const PAGE_SIZE = 10;
const DEBOUNCE_TIME = 500;

type TemplateResult = {data: Template[]; totalCount: number};

type DistributionRuleType =
  | 'round_robin_availability'
  | 'panel_interview'
  | null;

interface IProps {
  onSubmit: (
    templateId: string,
    templateName: string,
    template?: Template,
  ) => void;
  loading?: boolean;
  standardEntityType: string;
  distributionRule?: DistributionRuleType;
}

const TemplateSelector = ({
  onSubmit,
  loading = false,
  standardEntityType,
  distributionRule = null,
}: IProps) => {
  const {t, makeKey} = useLabelTranslation();
  const hasOwnCalendarPermission = usePrivilege('MANAGE_CALENDAR');
  const hasTeamTemplatePermission = usePrivilege('MANAGE_TEAM_TEMPLATES');

  const [selectedTab, setSelectedTab] = useState<string>(
    hasTeamTemplatePermission ? 'panel_interview' : 'one_slot_multiple_event',
  );
  const [page, setPage] = useState<number>(1);
  const [searchKey, setSearchKey] = React.useState('');
  const [debouncedSearchKey, setDebouncedSearchKey] = React.useState('');

  // When distributionRule is provided from parent, use it; otherwise use selectedTab
  const effectiveDistributionRule = distributionRule ?? selectedTab;
  const isTeamTemplate =
    effectiveDistributionRule === 'panel_interview' ||
    effectiveDistributionRule === 'round_robin_availability' ||
    effectiveDistributionRule === 'round_robin_fairness' ||
    selectedTab === 'round-robin';

  const debouncedSearch = React.useMemo(
    () =>
      debounce((query: string) => {
        setPage(1);
        setDebouncedSearchKey(query);
      }, DEBOUNCE_TIME),
    [],
  );

  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearchKey]);

  // Determine distribution_rule filter based on effective rule
  const getDistributionRuleFilter = () => {
    if (distributionRule) {
      return [distributionRule];
    }
    // Default behavior with tabs
    if (selectedTab === 'round-robin') {
      return ['round_robin_availability', 'round_robin_fairness'];
    }
    return [selectedTab];
  };

  const {data, isLoading} = useApiQuery(
    `scheduler/event-templates/${isTeamTemplate ? 'team/' : ''}list`,
    {
      queryOptions: {
        queryKey: [
          'templates',
          effectiveDistributionRule,
          page,
          debouncedSearchKey,
        ],
      },
      apiOptions: {
        method: 'POST',
        data: {
          offset: (page - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
          filters: {
            standard_entity_type: standardEntityType,
            name: debouncedSearchKey,
            distribution_rule: getDistributionRuleFilter(),
          },
        },
      },
    },
  );

  const handleTemplateSelect = (template: Template) => {
    onSubmit(template.id, template.name, template);
  };

  const handleTemplatePreview = (templateId: string) => {
    window.open(`/meetings/template/${templateId}`, '_blank');
  };

  const headers = getHeaders(t, makeKey);
  const templateResult: TemplateResult = camel(data);
  const totalCount = templateResult?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  if (loading) {
    return (
      <div className={css.loadingContainer}>
        <CircularLoader />
        <BodyMedium>
          {t('Selecting_Template', 'Selecting Template')}...
        </BodyMedium>
      </div>
    );
  }

  if (!hasOwnCalendarPermission) {
    return (
      <EmptyState
        title={t('Insufficient_privileges', 'Insufficient privileges')}
        description={t(
          'You_do_not_have_the_privilege_to_select_a_template',
          'You do not have the privilege to select a template',
        )}
        imageVariant="calendar"
      />
    );
  }

  return (
    <div className={css.templateSelectorContainer}>
      <div className={css.fluidSize}>
        <SearchInput
          value={searchKey}
          onClear={() => {
            setSearchKey('');
            debouncedSearch('');
          }}
          onChange={(event) => {
            const value = event.target.value;
            setSearchKey(value);
            debouncedSearch(value);
          }}
          classNames={{wrapper: css.fluidSize}}
          placeholder={t(
            'Search_any_meeting_by_name',
            'Search any meeting by name',
          )}
        />
      </div>
      {!distributionRule && (
        <div className={css.fluidSize}>
          <ButtonTabs
            classNames={{wrapper: css.fluidSize}}
            selectedButtonTabId={selectedTab}
            onButtonTabSelect={(key) => {
              setSelectedTab(key);
              setPage(1);
              setSearchKey('');
              debouncedSearch('');
            }}
          >
            {hasTeamTemplatePermission && (
              <ButtonTab id="panel_interview" classNames={{wrapper: css.tab}}>
                {t('Panel_Interviews', 'Panel Interviews')}
              </ButtonTab>
            )}
            {hasTeamTemplatePermission && (
              <ButtonTab id="round-robin" classNames={{wrapper: css.tab}}>
                {t('Round_Robin', 'Round Robin')}
              </ButtonTab>
            )}
            <ButtonTab
              id="one_slot_multiple_event"
              classNames={{
                wrapper: hasTeamTemplatePermission
                  ? css.tab
                  : css.noTeamPermissionTab,
              }}
            >
              {t('Multi_Event', 'Multi Event')}
            </ButtonTab>
            <ButtonTab
              id="single_account"
              classNames={{
                wrapper: hasTeamTemplatePermission
                  ? css.tab
                  : css.noTeamPermissionTab,
              }}
            >
              1:1
            </ButtonTab>
          </ButtonTabs>
        </div>
      )}
      <div className={css.tableContainer}>
        <Table
          borderRadius="12px 12px 0 0"
          headers={headers}
          entries={templateResult?.data || []}
          isLoading={isLoading}
          extras={{
            handleTemplateSelect,
            handleTemplatePreview,
          }}
          stickyHeader={true}
        />
        <TableBottomBar>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onChange={(page) => {
              setPage(page || 1);
            }}
          >
            <SubTitleExtraSmall color="secondary">
              {t('Showing_page_page_of_totalPages', {page, totalPages})}
            </SubTitleExtraSmall>
          </Pagination>
        </TableBottomBar>
      </div>
    </div>
  );
};

export default TemplateSelector;

const getHeaders = (
  t: (key: string, value: string) => string,
  makeKey: (key: string) => string,
): GenericHeaderItems<
  Template,
  {
    handleTemplateSelect: (template: Template) => void;
    handleTemplatePreview: (templateId: string) => void;
  }
> => {
  return [
    {
      key: 'name',
      label: t('Meeting_Name', 'Meeting Name'),
      render: ({data}) => (
        <BasicSingleCell>
          <BodyMedium className={css.nameCell}>
            <TruncatedTextWithTooltip
              tooltip={{
                bodyMaxLines: 3,
              }}
            >
              {/* Name is user defined */}
              {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
              {data.name}
            </TruncatedTextWithTooltip>
          </BodyMedium>
        </BasicSingleCell>
      ),
    },
    {
      key: 'duration',
      label: t('Duration', 'Duration'),
      render: ({data}) => (
        <SingleCell
          title={`${data.duration.amount} ${
            data.duration.unit === 'minutes' ? t('Min', 'Min') : t('Hrs', 'Hrs')
          }`}
        />
      ),
    },
    {
      key: 'location',
      label: t('Location', 'Location'),
      render: ({data}) => {
        const {location} = data;
        const isOnSite = !location || location === 'custom_location';
        const locationDisplayName = isOnSite ? 'On-site' : 'Virtual';

        return (
          <SingleCell
            title={t(makeKey(locationDisplayName), locationDisplayName)}
          />
        );
      },
    },
    {
      key: 'id',
      label: '',
      render: ({data, extras}) => {
        const disablePreview = data.templateType === 'child';
        return (
          <BasicSingleCell>
            <Button
              size="small"
              type="ghost"
              onClick={() => extras?.handleTemplatePreview(data.id)}
              disabled={disablePreview}
              iconRightName={disablePreview ? 'lock' : ''}
            >
              {t('Preview', 'Preview')}
            </Button>
          </BasicSingleCell>
        );
      },
    },
    {
      key: 'id',
      label: '',
      render: ({data, extras}) => (
        <BasicSingleCell>
          <Button
            size="small"
            type="secondary"
            iconRightName="angle-right"
            onClick={() => extras?.handleTemplateSelect(data)}
          >
            {t('Select', 'Select')}
          </Button>
        </BasicSingleCell>
      ),
    },
  ];
};
